import { environment } from '@hakimo-ui/hakimo/environments';
import { DataVersionOverride } from '@hakimo-ui/hakimo/types';
import { dataVersionOverrideAtom } from '@hakimo-ui/hakimo/util';
import {
  SegmentedToggle,
  SegmentedToggleOption,
} from '@hakimo-ui/shared/ui-base';
import { useAtom } from 'jotai';

const OPTIONS: SegmentedToggleOption[] = [
  { id: 'v2', label: 'v2' },
  { id: 'v3', label: 'v3' },
];

/**
 * Migration toggle. Lets a user choose whether a surface reads legacy (`v2`)
 * or unified (`v3`) data, to compare the two during the cloud-unification
 * rollout. Defaults to `v2`.
 *
 * Renders nothing when the deployment forces v3 (`FORCE_DATA_VERSION_V3`):
 * there is no choice to make, and `dataVersionOverrideAtom` is pinned to v3.
 *
 * Shown to everyone: `useApiDataVersion` sends the header on whatever is
 * picked here, and the backend honours it for any authenticated user. The
 * choice is persisted (see dataVersionOverrideAtom).
 *
 * It routes READS AND WRITES. The view mark (`useViewLocationAlarm`) goes to
 * v3 from the alarms list, and the incident deep links carry `?version=v3`
 * into the detail page, where field edits and report email/generate follow it.
 * Picking v3 moves a surface's mutations onto the unified tables too.
 */
export function DataVersionToggle() {
  const [version, setVersion] = useAtom(dataVersionOverrideAtom);

  if (environment.forceDataVersionV3) {
    return null;
  }

  return (
    <SegmentedToggle
      value={version}
      options={OPTIONS}
      onChange={(value) => setVersion(value as DataVersionOverride)}
      size="small"
      ariaLabel="Data source"
      title="Data source for this list. v2 = legacy tables, v3 = unified tables."
    />
  );
}

export default DataVersionToggle;
